/**
 * sky.ts · 09_APP · OS-006 · capability C35
 *
 * The top banner follows the real sky. Hers, 2026-10-15: "time of the day and weather light.
 * So if it was right now, it's daytime, clear skies."
 *
 * Weather comes from Open-Meteo: free, no key, so nothing secret ever sits in the browser.
 * If the call fails the banner still shows the right time of day; it never blocks the page.
 */

export type TimeOfDay = "dawn" | "day" | "dusk" | "night";
export type Condition = "clear" | "cloudy" | "rain" | "fog" | "storm";

export interface Sky {
  tod: TimeOfDay;
  cond: Condition;
  tempF: number | null;
}

const SACRAMENTO = { lat: 38.58, lon: -121.49 };

export function timeOfDay(now: Date, sunrise?: string, sunset?: string): TimeOfDay {
  const t = now.getTime();
  if (sunrise && sunset) {
    const sr = new Date(sunrise).getTime();
    const ss = new Date(sunset).getTime();
    const h = 3600e3;
    if (t < sr - h || t > ss + h) return "night";
    if (t < sr + h) return "dawn";
    if (t > ss - h) return "dusk";
    return "day";
  }
  const hr = now.getHours();
  return hr < 6 || hr >= 20 ? "night" : hr < 8 ? "dawn" : hr >= 18 ? "dusk" : "day";
}

/** WMO weather codes, as Open-Meteo returns them. */
export function conditionFromCode(c: number | null | undefined): Condition {
  if (c == null || c <= 1) return "clear";
  if (c <= 3) return "cloudy";
  if (c === 45 || c === 48) return "fog";
  if (c >= 95) return "storm";
  if ((c >= 51 && c <= 67) || (c >= 80 && c <= 82)) return "rain";
  return "cloudy";
}

export async function fetchSky(now = new Date()): Promise<Sky> {
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${SACRAMENTO.lat}&longitude=${SACRAMENTO.lon}` +
      `&current=temperature_2m,weather_code&daily=sunrise,sunset&temperature_unit=fahrenheit` +
      `&timezone=America%2FLos_Angeles&forecast_days=1`;
    const r = await fetch(url);
    const j = await r.json();
    return {
      tod: timeOfDay(now, j.daily?.sunrise?.[0], j.daily?.sunset?.[0]),
      cond: conditionFromCode(j.current?.weather_code),
      tempF: typeof j.current?.temperature_2m === "number" ? j.current.temperature_2m : null,
    };
  } catch {
    return { tod: timeOfDay(now), cond: "clear", tempF: null };
  }
}

export const SKY_COLORS: Record<TimeOfDay, [string, string, string]> = {
  dawn: ["#2B3A67", "#C8607A", "#FFB37A"],
  day: ["#2C79C7", "#5FA8DE", "#BFE3F5"],
  dusk: ["#1C2E5A", "#8E4A7C", "#FF8A3D"],
  night: ["#060E1F", "#0F2546", "#1C4B82"],
};

const DIM: Record<Condition, number> = { clear: 0, cloudy: 0.28, rain: 0.42, fog: 0.2, storm: 0.6 };

export function skyGradient(tod: TimeOfDay, cond: Condition): string {
  const dim = DIM[cond];
  const mix = (hex: string) => {
    if (!dim) return hex;
    const n = parseInt(hex.slice(1), 16);
    const v = [n >> 16, (n >> 8) & 255, n & 255].map((x) => Math.round(x * (1 - dim) + 128 * dim));
    return `rgb(${v.join(",")})`;
  };
  const c = SKY_COLORS[tod];
  return `linear-gradient(180deg,${mix(c[0])} 0%,${mix(c[1])} 60%,${mix(c[2])} 100%)`;
}

export const CONDITION_WORD: Record<Condition, string> = {
  clear: "Clear", cloudy: "Cloudy", rain: "Rain", fog: "Fog", storm: "Storm",
};
